import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { 
  Search, 
  Star, 
  Users, 
  TrendingUp, 
  Eye,
  Plus,
  ArrowRight,
  Target,
  DollarSign,
  Calendar,
  CheckCircle
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Brand {
  id: string;
  name: string;
  vertical: string;
  status: string;
  competitive_categories: string[];
  preferred_verticals: string[];
  budget_range: string;
  target_demographics: string[];
}

interface MarketplaceMetrics {
  total_eligible_projects: number;
  average_artie_score: number;
  total_potential_reach: number;
  active_campaigns: number;
  preference_signals_count: number;
}

interface Project {
  id: string;
  title: string;
  logline: string;
  creator: string;
  vertical: string;
  artie_score: number;
  brand_fit_score: number;
  forecasted_reach_90d: number;
  production_status: string;
  hero_image_url?: string;
  is_validated: boolean;
}

interface Campaign {
  id: string;
  name: string;
  status: string;
  budget_usd: number;
  created_at: string;
}

interface MarketplaceAccess {
  brand: Brand;
  marketplace_metrics: MarketplaceMetrics;
  eligible_projects: Project[];
  recent_campaigns: Campaign[];
  marketplace_actions: {
    can_create_campaign: boolean;
    can_browse_inventory: boolean;
    can_submit_preferences: boolean;
    can_request_packages: boolean;
  };
  next_steps: string[];
}

interface CampaignFormData {
  name: string;
  description: string;
  target_audience: string[];
  target_categories: string[];
  budget_usd: number;
  target_ecpm: number;
  campaign_duration_days: number;
  content_suitability: string[];
}

const BrandMarketplaceAccessPage: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [marketplaceData, setMarketplaceData] = useState<MarketplaceAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isCreateCampaignOpen, setIsCreateCampaignOpen] = useState(false);
  const [campaignForm, setCampaignForm] = useState<CampaignFormData>({
    name: '',
    description: '',
    target_audience: [],
    target_categories: [],
    budget_usd: 0,
    target_ecpm: 10.0,
    campaign_duration_days: 30,
    content_suitability: []
  });

  useEffect(() => {
    if (brandId) {
      fetchMarketplaceAccess();
    }
  }, [brandId]);

  const fetchMarketplaceAccess = async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<MarketplaceAccess>(`/api/v1/ops-dashboard/brand-marketplace-access/${brandId}`);
      setMarketplaceData(data);
    } catch (error) {
      console.error('Error fetching marketplace access:', error);
      toast({
        title: "Error",
        description: "Failed to load marketplace access data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCampaign = async () => {
    try {
      const result = await apiClient.post<{ campaign: { name: string } }>(`/api/v1/ops-dashboard/brand-marketplace-access/${brandId}/campaign`, campaignForm);
      toast({
        title: "Campaign Created",
        description: `Campaign "${result.campaign.name}" created successfully`
      });
      setIsCreateCampaignOpen(false);
      setCampaignForm({
        name: '',
        description: '',
        target_audience: [],
        target_categories: [],
        budget_usd: 0,
        target_ecpm: 10.0,
        campaign_duration_days: 30,
        content_suitability: []
      });
      // Refresh marketplace data
      fetchMarketplaceAccess();
    } catch (error) {
      console.error('Error creating campaign:', error);
      toast({
        title: "Error",
        description: "Failed to create campaign",
        variant: "destructive"
      });
    }
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'draft': return 'bg-yellow-100 text-yellow-800';
      case 'paused': return 'bg-gray-100 text-gray-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!marketplaceData) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Brand not found</h3>
        <p className="text-gray-600">Unable to load marketplace access for this brand.</p>
      </div>
    );
  }

  const { brand, marketplace_metrics, eligible_projects, recent_campaigns, marketplace_actions, next_steps } = marketplaceData;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Brand Marketplace Access</h1>
              <p className="text-gray-600 mt-1">
                {brand.name} • {brand.vertical} • {brand.status}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="bg-blue-50 text-blue-700">
                {marketplace_metrics.total_eligible_projects} Eligible Projects
              </Badge>
              {marketplace_actions.can_create_campaign && (
                <Dialog open={isCreateCampaignOpen} onOpenChange={setIsCreateCampaignOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Campaign
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Campaign</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Campaign Name</label>
                        <Input
                          value={campaignForm.name}
                          onChange={(e) => setCampaignForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Enter campaign name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Description</label>
                        <Textarea
                          value={campaignForm.description}
                          onChange={(e) => setCampaignForm(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Describe your campaign objectives"
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-2">Budget (USD)</label>
                          <Input
                            type="number"
                            value={campaignForm.budget_usd}
                            onChange={(e) => setCampaignForm(prev => ({ ...prev, budget_usd: Number(e.target.value) }))}
                            placeholder="0"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-2">Target eCPM</label>
                          <Input
                            type="number"
                            step="0.1"
                            value={campaignForm.target_ecpm}
                            onChange={(e) => setCampaignForm(prev => ({ ...prev, target_ecpm: Number(e.target.value) }))}
                            placeholder="10.0"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Campaign Duration (Days)</label>
                        <Input
                          type="number"
                          value={campaignForm.campaign_duration_days}
                          onChange={(e) => setCampaignForm(prev => ({ ...prev, campaign_duration_days: Number(e.target.value) }))}
                          placeholder="30"
                        />
                      </div>
                      <div className="flex gap-2 pt-4">
                        <Button onClick={handleCreateCampaign} className="flex-1">
                          Create Campaign
                        </Button>
                        <Button variant="outline" onClick={() => setIsCreateCampaignOpen(false)}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Brand Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Target className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Eligible Projects</p>
                  <p className="text-2xl font-bold text-gray-900">{marketplace_metrics.total_eligible_projects}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Star className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Avg Artie Score</p>
                  <p className="text-2xl font-bold text-gray-900">{marketplace_metrics.average_artie_score}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Users className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Reach</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(marketplace_metrics.total_potential_reach)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <Calendar className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
                  <p className="text-2xl font-bold text-gray-900">{marketplace_metrics.active_campaigns}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Brand Preferences */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Brand Preferences</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium mb-2">Preferred Verticals</h4>
                <div className="flex flex-wrap gap-2">
                  {brand.preferred_verticals.map((vertical, index) => (
                    <Badge key={index} variant="outline">{vertical}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Competitive Categories</h4>
                <div className="flex flex-wrap gap-2">
                  {brand.competitive_categories.map((category, index) => (
                    <Badge key={index} variant="secondary">{category}</Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">Budget Range</h4>
                <Badge className="bg-green-100 text-green-800">{brand.budget_range}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Recommended Next Steps</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {next_steps.map((step, index) => (
                <div key={index} className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                  <span className="text-gray-700">{step}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Eligible Projects */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Top Eligible Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {eligible_projects.slice(0, 6).map((project) => (
                <Card key={project.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                  <div className="aspect-video bg-gray-200 relative overflow-hidden">
                    {project.hero_image_url ? (
                      <img 
                        src={project.hero_image_url} 
                        alt={project.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-600 flex items-center justify-center">
                        <span className="text-white text-lg font-bold">
                          {project.title.charAt(0)}
                        </span>
                      </div>
                    )}
                    {project.is_validated && (
                      <div className="absolute top-2 left-2">
                        <Badge className="bg-green-100 text-green-800 border-green-200">
                          <Star className="h-3 w-3 mr-1" />
                          Validated
                        </Badge>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg mb-2 line-clamp-1">{project.title}</h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">{project.logline}</p>
                    
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        <Users className="h-4 w-4" />
                        <span>{formatNumber(project.forecasted_reach_90d)}</span>
                      </div>
                      <Badge variant="outline">
                        Score: {project.artie_score}
                      </Badge>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="flex-1"
                        onClick={() => navigate(`/brand-marketplace/package?project_id=${project.id}&brand_id=${brandId}`)}
                      >
                        View Package
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="px-3"
                        onClick={() => setSelectedProject(project)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {eligible_projects.length > 6 && (
              <div className="text-center mt-6">
                <Button variant="outline">
                  View All {marketplace_metrics.total_eligible_projects} Projects
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Campaigns */}
        {recent_campaigns.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Recent Campaigns</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recent_campaigns.map((campaign) => (
                  <div key={campaign.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <div>
                      <h4 className="font-medium">{campaign.name}</h4>
                      <p className="text-sm text-gray-600">
                        Budget: ${campaign.budget_usd.toLocaleString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getStatusColor(campaign.status)}>
                        {campaign.status}
                      </Badge>
                      <Button size="sm" variant="outline">
                        View Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Project Details Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          {selectedProject && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedProject.title}</DialogTitle>
              </DialogHeader>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {selectedProject.hero_image_url && (
                    <img 
                      src={selectedProject.hero_image_url} 
                      alt={selectedProject.title}
                      className="w-full aspect-video object-cover rounded-lg"
                    />
                  )}
                  
                  <div>
                    <h4 className="font-medium mb-2">Logline</h4>
                    <p className="text-gray-600">{selectedProject.logline}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium mb-2">Creator</h4>
                      <p className="text-gray-600">{selectedProject.creator}</p>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Vertical</h4>
                      <Badge variant="outline">{selectedProject.vertical}</Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-medium mb-2">Artie Score</h4>
                      <p className="text-2xl font-bold text-blue-600">{selectedProject.artie_score}</p>
                    </div>
                    <div>
                      <h4 className="font-medium mb-2">Brand Fit Score</h4>
                      <p className="text-2xl font-bold text-green-600">{selectedProject.brand_fit_score.toFixed(1)}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">90-Day Reach</h4>
                    <p className="text-gray-600">{formatNumber(selectedProject.forecasted_reach_90d)}</p>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Production Status</h4>
                    <Badge variant="outline">{selectedProject.production_status.replace('_', ' ')}</Badge>
                  </div>

                  <div className="pt-4">
                    <Button 
                      className="w-full"
                      onClick={() => {
                        setSelectedProject(null);
                        navigate(`/brand-marketplace/package?project_id=${selectedProject.id}&brand_id=${brandId}`);
                      }}
                    >
                      View Package for This Project
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BrandMarketplaceAccessPage;